import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import {
  birthDatePickerTextFieldSx,
  StyledInputCellphoneAddUser,
  StyledInputTextModalUser,
  StyledModal,
  StyledSelectModalUser,
} from './styles';
import { ButtonAddGroup, StyledInputText, StyledModalFooter } from '../styles';
import { Input } from 'reactstrap';
import { LabelInput, LabelNames, StyledOptions } from '../../userRegister/style';
import { useToast } from '../../../context/Toast';
import { UserRole } from '../../../services/utils/enum';
import { registerUser } from '../../../services/user/user';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { buildUnexpectedUserFormErrorMessage, ConflictField, getConflictValidation } from '../userFormErrors';
import { getApiErrorMessage } from '../../../services/utils/apiError';
import { getCountryOptions, resolveCountryCode } from '../../../i18n/country';
import { getLocalizedPhoneLabels } from '../../../i18n/phoneLabels';
import { useAppLocale } from '../../../i18n/useAppLocale';
import {
  birthDatePickerCalendarSx,
  getBirthDateDayOfWeekFormatter,
  getBirthDateFormat,
} from '../../../i18n/birthDate';

interface IFormState {
  first_name: string;
  last_name: string;
  email: string;
  birth_date: string;
  country?: string;
  cellphone?: string;
  role: number;
  sex?: number;
}

const initialFormState: IFormState = {
  first_name: '',
  last_name: '',
  email: '',
  cellphone: '',
  role: UserRole.employee,
  birth_date: '',
  sex: 3,
};

function ModalAddUser({
  show,
  onClose,
  addItem,
}: any) {
  const { t } = useTranslation('settings');
  const appLocale = useAppLocale();
  const { showToast } = useToast();
  const [showLoading, setShowLoading] = useState(false);
  const { user, SignOut } = useAuth();
  const [form, setForm] = useState<IFormState>(initialFormState);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ConflictField, string>>>({});

  useEffect(() => {
    if (!show) {
      setFieldErrors({});
      setShowLoading(false);
    }
  }, [show]);

  const resetForm = () => {
    setForm(initialFormState);
    setFieldErrors({});
  };

  const handleClose = () => {
    resetForm();
    setShowLoading(false);
    onClose();
  };

  const clearFieldError = (field: ConflictField) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handlePhoneChange = (value?: string) => {
    setForm((prevForm) => ({
      ...prevForm,
      cellphone: value || '',
    }));
    clearFieldError('cellphone');
  };

  const handleBirthDateChange = (value: Dayjs | null) => {
    setForm((current) => ({
      ...current,
      birth_date: value?.isValid() ? value.format('YYYY-MM-DD') : '',
    }));
  };

  const handleInputChange = (event: any) => {
    const { name, type, checked } = event.target;

    if (type === 'checkbox') {
      setForm({
        ...form,
        [name]: checked ? 'S' : 'N',
      });
    } else {
      setForm({
        ...form,
        [name]: event.target.value,
      });
    }

    if (name === 'email') {
      clearFieldError('email');
    }
  };

  const onSubmit = async () => {
    setShowLoading(true);
    const company = user.company_visited?.id;

    if (!company) {
      showToast(t('users.sessionExpired'), 'info');
      setTimeout(() => {
        showToast(t('users.redirecting'), 'info');
        setTimeout(() => SignOut('/admin/signup'), 5000);
      }, 2500);
      setShowLoading(false);
      return;
    }

    const { birth_date, country, first_name, last_name, sex, email, cellphone } = form;

    if (!first_name || !last_name || !email || !birth_date) {
      setShowLoading(false);
      showToast(t('users.required'), 'error');
      return;
    }

    try {
      let dataToSend: any = {
        company_id: company,
        first_name,
        last_name,
        email,
        sex: Number(sex),
        birth_date,
        country: resolveCountryCode(country),
      };

      if (cellphone && cellphone.length > 0) {
        dataToSend.cellphone = cellphone;
      }

      const userRegistered = addItem ? await addItem(dataToSend) : await registerUser(dataToSend);

      if (userRegistered !== false) {
        showToast(t('users.created'), 'success');

        resetForm();

        setShowLoading(false);
        handleClose();
      } else {
        setShowLoading(false);
      }
    } catch (error: any) {
      const conflictValidation = getConflictValidation(error);

      if (conflictValidation) {
        setFieldErrors(conflictValidation.nextFieldErrors);
        showToast(conflictValidation.toastMessage, 'warning', 8000);
        setShowLoading(false);
        return;
      }

      console.error(error);
      const unexpectedMessage = getApiErrorMessage(
        error,
        buildUnexpectedUserFormErrorMessage('cadastrar o usuário')
      );
      showToast(unexpectedMessage, 'error', 8000);
      setShowLoading(false);
      throw error;
    }
  };

  if (!show) return null;
  return (
    <>
      <StyledModal show={show} onHide={handleClose} centered size='lg' >
        <Modal.Header>
          <Modal.Title style={{ color: '#3C4043' }}>{t('users.addTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="first_name">
                <LabelNames style={{ color: '#3C4043' }}>{t('users.firstName')}</LabelNames>
                <StyledInputText style={{ background: '#F1F1F1', paddingRight: 10 }}
                  type="text"
                  name="first_name"
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="last_name">
                <LabelNames style={{ color: '#3C4043' }}>{t('users.lastName')}</LabelNames>
                <StyledInputText style={{ background: '#F1F1F1', paddingRight: 10 }}
                  type="text"
                  name="last_name"
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className='mt-2 '>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="email">
                <LabelNames style={{ color: '#3C4043' }}>{t('table.email')}</LabelNames>
                <StyledInputTextModalUser
                  $hasError={!!fieldErrors.email}
                  invalid={!!fieldErrors.email}
                  style={{ background: '#F1F1F1', paddingRight: 10 }}
                  type="email"
                  name="email"
                  aria-invalid={!!fieldErrors.email}
                  onChange={handleInputChange}
                />
                {fieldErrors.email && (
                  <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {fieldErrors.email}
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="country">
                <LabelNames style={{ color: '#3C4043' }}>{t('users.country')}</LabelNames>
                <StyledSelectModalUser style={{ background: '#F1F1F1', }}
                  type="select"
                  name="country"
                  value={form?.country || ''}
                  onChange={handleInputChange}
                >
                  <option value='' disabled hidden>{t('users.select')}</option>
                  {getCountryOptions(appLocale).map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </StyledSelectModalUser>
              </Form.Group>
            </Col>
          </Row>

          <Row className='mt-2 '>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="birthday">
                <LabelNames style={{ color: '#3C4043', textAlign: "left", }}>{t('users.birthDate')}</LabelNames>
                <DatePicker
                  value={form.birth_date ? dayjs(form.birth_date) : null}
                  onChange={handleBirthDateChange}
                  format={getBirthDateFormat(appLocale)}
                  dayOfWeekFormatter={getBirthDateDayOfWeekFormatter(appLocale)}
                  disabled={showLoading}
                  minDate={dayjs('1925-01-01')}
                  maxDate={dayjs()}
                  fixedWeekNumber={6}
                  reduceAnimations
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: birthDatePickerTextFieldSx,
                    },
                    layout: { sx: birthDatePickerCalendarSx },
                  }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="cellphone">
                <LabelNames style={{ color: '#3C4043' }}>{t('users.phone')}</LabelNames>
                <StyledInputCellphoneAddUser
                  $hasError={!!fieldErrors.cellphone}
                  name="cellphone"
                  placeholder={String(t('users.phonePlaceholder'))}
                  value={form.cellphone}
                  onChange={handlePhoneChange}
                  limitMaxLength={true}
                  labels={getLocalizedPhoneLabels(appLocale)}
                  defaultCountry="BR"
                  className="input-admin"
                />
                {fieldErrors.cellphone && (
                  <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {fieldErrors.cellphone}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className='mt-2 '>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3" controlId="sex">
                <LabelNames style={{ color: '#3C4043' }}>{t('users.sex')}</LabelNames>
                <StyledSelectModalUser style={{ background: '#F1F1F1', height: '46px' }}
                  size='sm'
                  type='select'
                  name='sex'
                  value={form?.sex}
                  onChange={handleInputChange}>
                  <StyledOptions value={0} disabled defaultValue={3} hidden>{t('users.select')}</StyledOptions>
                  <StyledOptions value={1}>{t('modal.male')}</StyledOptions>
                  <StyledOptions value={2}>{t('modal.female')}</StyledOptions>
                  <StyledOptions value={3}>{t('users.notInformed')}</StyledOptions>
                </StyledSelectModalUser>
              </Form.Group>
            </Col>

            <Col xs={12} sm={6}></Col>
          </Row>
        </Modal.Body>
        <StyledModalFooter>
          <ButtonAddGroup>
            <Button
              sx={{
                textTransform: 'capitalize',
                padding: "0.5em 2em",
                borderRadius: '2em',
                fontSize: '1em',
                fontWeight: 500,
                border: '1px solid #a0a0a0',
              }}
              variant='text'
              onClick={handleClose}
              disabled={showLoading}
            >
              {t('users.close')}
            </Button>
            <Button
              sx={{
                textTransform: 'capitalize',
                background: '#2491FF',
                borderRadius: '2em',
                fontSize: '1em',
                fontWeight: 'semibold ',
                padding: "0.5em 2em",
              }}
              variant='contained'
              onClick={() => {
                void onSubmit().catch(() => undefined);
              }}
              disabled={showLoading}
            >
              {showLoading ? t('users.confirming') : t('users.confirm')}
            </Button>
          </ButtonAddGroup>
        </StyledModalFooter>
      </StyledModal>
    </>
  );
}

export default ModalAddUser;

